import { useCallback, useMemo } from 'react';
import type {
  CampaignSession,
  CampaignSessionStatus,
  InWorldTime,
  SessionState,
} from '@roll1/shared';

export interface UseCampaignSessionsOptions {
  state: SessionState | null;
  send: (msg: Record<string, unknown>) => void;
}

export interface UseCampaignSessionsReturn {
  /** All sessions sorted by status (active first, then planned, then completed by number desc) */
  sessions: CampaignSession[];
  /** Currently active session (at most one) */
  activeSession: CampaignSession | null;
  /** Planned sessions */
  plannedSessions: CampaignSession[];
  /** Completed sessions */
  completedSessions: CampaignSession[];
  /** Next auto-increment number */
  nextNumber: number;
  /** CRUD operations */
  createSession: (session: Omit<CampaignSession, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateSession: (sessionId: string, changes: Partial<Pick<CampaignSession, 'title' | 'date' | 'timeStart' | 'timeEnd' | 'participants'>>) => void;
  updateStatus: (sessionId: string, status: CampaignSessionStatus) => void;
  deleteSession: (sessionId: string) => void;
  /** «Начать сессию»: the first planned one, or a new running one. */
  startSession: () => void;
}

const STATUS_ORDER: Record<CampaignSessionStatus, number> = {
  active: 0,
  planned: 1,
  completed: 2,
};

function compareSessions(a: CampaignSession, b: CampaignSession): number {
  const statusDiff = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
  if (statusDiff !== 0) return statusDiff;
  // Within same status, sort by number descending
  return b.number - a.number;
}

/** Today as the session's real date («2026-10-24», the local day). */
function today(): string {
  const d = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * «Начать сессию» where the GM plays: a planned session is started, otherwise the next one is created running
 * (the server writes its start into the chronicle either way). One click, no form.
 */
export function beginSession(
  sessions: readonly CampaignSession[],
  nextNumber: number,
  create: UseCampaignSessionsReturn['createSession'],
  updateStatus: UseCampaignSessionsReturn['updateStatus'],
): void {
  const planned = sessions.filter(s => s.status === 'planned').sort((a, b) => a.number - b.number)[0];
  if (planned) {
    updateStatus(planned.id, 'active');
    return;
  }
  create({ number: nextNumber, date: today(), participants: [], status: 'active' });
}

export function useCampaignSessions({ state, send }: UseCampaignSessionsOptions): UseCampaignSessionsReturn {
  const sessions = useMemo(() => {
    if (!state) return [];
    return [...(state.sessions ?? [])].sort(compareSessions);
  }, [state?.sessions]);

  const activeSession = useMemo(
    () => sessions.find(s => s.status === 'active') ?? null,
    [sessions],
  );

  const plannedSessions = useMemo(
    () => sessions.filter(s => s.status === 'planned'),
    [sessions],
  );

  const completedSessions = useMemo(
    () => sessions.filter(s => s.status === 'completed'),
    [sessions],
  );

  const nextNumber = useMemo(() => {
    if (sessions.length === 0) return 1;
    return Math.max(...sessions.map(s => s.number)) + 1;
  }, [sessions]);

  const createSession = useCallback((session: Omit<CampaignSession, 'id' | 'createdAt' | 'updatedAt'>) => {
    send({ type: 'campaign-session:create', session });
  }, [send]);

  const updateSession = useCallback((sessionId: string, changes: Partial<Pick<CampaignSession, 'title' | 'date' | 'timeStart' | 'timeEnd' | 'participants'>>) => {
    send({ type: 'campaign-session:update', sessionId, changes });
  }, [send]);

  const updateStatus = useCallback((sessionId: string, status: CampaignSessionStatus) => {
    send({ type: 'campaign-session:update-status', sessionId, status });
  }, [send]);

  const deleteSession = useCallback((sessionId: string) => {
    send({ type: 'campaign-session:delete', sessionId });
  }, [send]);

  const startSession = useCallback(
    () => beginSession(sessions, nextNumber, createSession, updateStatus),
    [sessions, nextNumber, createSession, updateStatus],
  );

  return {
    startSession,
    sessions,
    activeSession,
    plannedSessions,
    completedSessions,
    nextNumber,
    createSession,
    updateSession,
    updateStatus,
    deleteSession,
  };
}

/**
 * «Начать сессию» for a place that only needs the button (the search's command): whether a session runs now, and the
 * start itself — the same beginSession, sent as typed messages on the session's socket.
 */
export function useSessionStart(
  state: SessionState | null,
  send: (msg: { type: 'campaign-session:create'; session: Omit<CampaignSession, 'id' | 'createdAt' | 'updatedAt'> } | { type: 'campaign-session:update-status'; sessionId: string; status: CampaignSessionStatus }) => unknown,
): { readonly running: boolean; readonly start: () => void } {
  const sessions = state?.sessions;
  const running = (sessions ?? []).some(s => s.status === 'active');
  const start = useCallback(() => {
    const list = sessions ?? [];
    const next = list.length === 0 ? 1 : Math.max(...list.map(s => s.number)) + 1;
    beginSession(
      list,
      next,
      (session) => { send({ type: 'campaign-session:create', session }); },
      (sessionId, status) => { send({ type: 'campaign-session:update-status', sessionId, status }); },
    );
  }, [sessions, send]);
  return { running, start };
}
